import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import api, { imageUrl } from "@/lib/api";
import { ArrowRight, Cpu, Cog, Wrench, Layers, ShieldCheck, Headphones } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1767294274634-613a3545e36d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function Home() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const catIcon = { assembly: Cpu, cutting: Cog, soldering: Wrench };
  const cats = ["assembly", "cutting", "soldering"];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <img src={HERO} alt="Factory" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-white/65" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-36">
          <motion.div initial="hidden" animate="show" className="max-w-3xl">
            <motion.p custom={0} variants={fade} className="mb-4 inline-block border border-blue-700 px-3 py-1 text-xs font-bold uppercase tracking-widest text-blue-700">
              {t("home.tagline")}
            </motion.p>
            <motion.h1 custom={1} variants={fade} className="text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              {t("home.title")}
            </motion.h1>
            <motion.p custom={2} variants={fade} className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
              {t("home.subtitle")}
            </motion.p>
            <motion.div custom={3} variants={fade} className="mt-8 flex flex-wrap gap-4">
              <Link to="/catalog" className="group flex items-center gap-2 rounded-none bg-blue-700 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-blue-800" data-testid="hero-cta-catalog">
                {t("home.cta")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" className="rounded-none border border-slate-900 px-6 py-3 text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white" data-testid="hero-cta-contact">
                {t("home.cta2")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {[["stat1", "stat1l"], ["stat2", "stat2l"], ["stat3", "stat3l"]].map(([n, l], i) => (
            <div key={i} className="px-8 py-10 text-center" data-testid={`stat-${i}`}>
              <div className="text-4xl font-bold tracking-tight text-blue-700">{t(`home.${n}`)}</div>
              <div className="mt-2 text-sm font-medium uppercase tracking-wider text-slate-500">{t(`home.${l}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">{t("home.prodTitle")}</h2>
          <p className="mt-3 text-base text-slate-600">{t("home.prodSubtitle")}</p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {cats.map((cat, i) => {
            const Icon = catIcon[cat];
            const sample = items.find((it) => it.category === cat);
            return (
              <motion.div key={cat} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={`/catalog?cat=${cat}`} className="group block h-full border border-slate-200 bg-white transition-transform duration-200 hover:-translate-y-1 hover:border-blue-700" data-testid={`product-cat-${cat}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {sample && <img src={imageUrl(sample.image_url)} alt={cat} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />}
                  </div>
                  <div className="p-6">
                    <Icon className="mb-3 h-7 w-7 text-blue-700" />
                    <h3 className="text-lg font-bold tracking-tight text-slate-950">{t(`categories.${cat}`)}</h3>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-blue-700">
                      {t("common.learnMore")} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Why */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <h2 className="mb-12 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">{t("home.whyTitle")}</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[[Layers, "why1t", "why1"], [ShieldCheck, "why2t", "why2"], [Headphones, "why3t", "why3"]].map(([Icon, tt, dd], i) => (
              <div key={i} className="border-l-2 border-blue-700 pl-6" data-testid={`why-${i}`}>
                <Icon className="mb-4 h-8 w-8 text-slate-900" />
                <h3 className="mb-2 text-lg font-bold tracking-tight text-slate-950">{t(`home.${tt}`)}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{t(`home.${dd}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-700">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-16 sm:px-6 md:flex-row md:items-center">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t("home.ctaTitle")}</h2>
            <p className="mt-2 max-w-xl text-blue-100">{t("home.ctaText")}</p>
          </div>
          <Link to="/contact" className="shrink-0 rounded-none bg-white px-8 py-4 text-sm font-bold uppercase tracking-widest text-blue-700 transition-colors hover:bg-slate-100" data-testid="cta-contact-btn">
            {t("common.contactUs")}
          </Link>
        </div>
      </section>
    </div>
  );
}
